import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Plus, 
  Trash2, 
  AlertCircle, 
  Percent, 
  Loader2, 
  CheckCircle, 
  XCircle,
  Search,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useRealMarketData } from './hooks/useRealMarketData';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

interface AggregatedSymbol {
  id: string;
  symbol: string;
  profitTarget: string;
  stopLoss: string;
}

interface StewieQuantSymbolsAggregationProps {
  onSave: (symbols: AggregatedSymbol[]) => void;
  onFetchMarketData: (symbols: AggregatedSymbol[]) => void;
  initialSymbols?: AggregatedSymbol[];
}

const StewieQuantSymbolsAggregation: React.FC<StewieQuantSymbolsAggregationProps> = ({
  onSave,
  onFetchMarketData,
  initialSymbols = []
}) => {
  const { toast } = useToast();
  const [symbols, setSymbols] = useState<AggregatedSymbol[]>(initialSymbols);
  const [symbolInput, setSymbolInput] = useState('');
  const [profitTarget, setProfitTarget] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  
  const { 
    validateSymbol, 
    symbolStatus, 
    isLoading,
    fetchMarketData
  } = useRealMarketData();

  // Load symbols from initialSymbols and validate them on component mount
  useEffect(() => {
    if (initialSymbols.length > 0) {
      // Validate all initial symbols
      const validateInitialSymbols = async () => {
        setIsValidating(true);
        
        for (const symbol of initialSymbols) {
          await validateSymbol(symbol.symbol);
        }
        
        setIsValidating(false);
      };
      
      validateInitialSymbols();
    }
  }, [initialSymbols]);

  const handleSearchSymbol = async () => {
    if (!symbolInput.trim()) return;
    
    setIsValidating(true);
    setShowSearchResults(true);
    
    try {
      // Import dynamically to avoid including in the main bundle
      const { alphaVantageService } = await import('@/services/alphaVantage/alphaVantageService');
      
      const results = await alphaVantageService.searchSymbol(symbolInput.trim());
      
      if (results.bestMatches && results.bestMatches.length > 0) {
        setSearchResults(results.bestMatches);
      } else {
        setSearchResults([]);
        toast({
          title: "No Results Found",
          description: "No matching symbols found. Try a different search term.",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: "Search Error",
        description: error.message || "Failed to search for symbols",
        variant: "destructive"
      });
      setSearchResults([]);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSelectSearchResult = (result: any) => {
    const symbol = result['1. symbol'];
    setSymbolInput(symbol);
    setShowSearchResults(false);
  };

  const handleAddSymbol = async () => {
    if (!symbolInput.trim()) {
      toast({
        title: "Symbol Required",
        description: "Please enter a valid symbol",
        variant: "destructive"
      });
      return;
    }

    // Validate profit target and stop loss as numbers
    if (profitTarget && isNaN(Number(profitTarget))) {
      toast({
        title: "Invalid Profit Target",
        description: "Profit target must be a number",
        variant: "destructive"
      });
      return;
    }

    if (stopLoss && isNaN(Number(stopLoss))) {
      toast({
        title: "Invalid Stop Loss",
        description: "Stop loss must be a number",
        variant: "destructive"
      });
      return;
    }

    // Check for duplicates
    const symbolTrimmed = symbolInput.trim().toUpperCase();
    if (symbols.some(s => s.symbol.toLowerCase() === symbolTrimmed.toLowerCase())) {
      toast({
        title: "Duplicate Symbol",
        description: `Symbol ${symbolTrimmed} already exists`,
        variant: "destructive"
      });
      return;
    }

    setIsValidating(true);
    
    try {
      // Validate the symbol with Alpha Vantage
      const isValid = await validateSymbol(symbolTrimmed);
      
      if (!isValid) {
        toast({
          title: "Invalid Symbol",
          description: `Symbol ${symbolTrimmed} does not exist or cannot be found`,
          variant: "destructive"
        });
        setIsValidating(false);
        return;
      }
      
      const newSymbol: AggregatedSymbol = {
        id: Date.now().toString(),
        symbol: symbolTrimmed,
        profitTarget: profitTarget || '',
        stopLoss: stopLoss || ''
      };

      const updatedSymbols = [...symbols, newSymbol];
      setSymbols(updatedSymbols);
      
      // Automatically sync with parent component
      onSave(updatedSymbols);
      
      setSymbolInput('');
      setProfitTarget('');
      setStopLoss('');
      
      toast({
        title: "Symbol Added",
        description: `Successfully added ${symbolTrimmed} to your basket`,
      });
    } catch (error: any) {
      toast({
        title: "Validation Error",
        description: error.message || "Failed to validate symbol",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveSymbol = (id: string) => {
    const updatedSymbols = symbols.filter(symbol => symbol.id !== id);
    setSymbols(updatedSymbols);
    
    // Automatically sync with parent component
    onSave(updatedSymbols);
  };

  const handleUpdateSymbol = (id: string, field: 'profitTarget' | 'stopLoss', value: string) => {
    const updatedSymbols = symbols.map(symbol => 
      symbol.id === id ? { ...symbol, [field]: value } : symbol
    );
    setSymbols(updatedSymbols);
    
    // Automatically sync with parent component
    onSave(updatedSymbols);
  };

  const handleSaveSymbols = () => {
    onSave(symbols);
    
    // Fetch market data for saved symbols
    if (onFetchMarketData) {
      onFetchMarketData(symbols);
    } else {
      // If no callback provided, use the hook directly
      fetchMarketData(symbols);
    }
    
    toast({
      title: "Market Data Refreshed",
      description: `Updated market data for ${symbols.length} symbols`,
    });
  };

  // Get symbol status indicator
  const getSymbolStatusIndicator = (symbol: string) => {
    const status = symbolStatus[symbol];
    
    if (!status || status === 'loading') {
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }
    
    if (status === 'valid') {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Symbol Aggregation</h3>
          <span className="text-sm text-gray-500">{symbols.length} symbols</span>
        </div>
        <p className="text-sm text-gray-500">
          Add symbols to analyze with the StewieQuant Algorithm and set individual profit targets and stop losses.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-4">
              <Label htmlFor="symbol">Symbol</Label>
              <div className="relative">
                <Popover open={showSearchResults} onOpenChange={setShowSearchResults}>
                  <PopoverTrigger asChild>
                    <div className="flex">
                      <Input
                        id="symbol"
                        placeholder="e.g. AAPL"
                        value={symbolInput}
                        onChange={(e) => setSymbolInput(e.target.value)}
                        className="rounded-lg rounded-r-none"
                      />
                      <Button 
                        type="button" 
                        onClick={handleSearchSymbol}
                        className="rounded-l-none"
                        disabled={isValidating || !symbolInput.trim()}
                      >
                        {isValidating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <ScrollArea className="h-64">
                      {searchResults.length > 0 ? (
                        <div className="py-2">
                          {searchResults.map((result, index) => (
                            <div 
                              key={index}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleSelectSearchResult(result)}
                            >
                              <div className="font-medium">{result['1. symbol']}</div>
                              <div className="text-xs text-gray-500 truncate">{result['2. name']}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {result['3. type']}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {result['4. region']}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center p-4 text-center h-full">
                          <Search className="h-10 w-10 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">
                            {isValidating ? 'Searching...' : 'No results found'}
                          </p>
                        </div>
                      )}
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="col-span-3">
              <Label htmlFor="profit-target">Profit Target (%)</Label>
              <Input
                id="profit-target"
                placeholder="e.g. 5"
                value={profitTarget}
                onChange={(e) => setProfitTarget(e.target.value)}
                className="rounded-lg"
              />
            </div>
            <div className="col-span-3">
              <Label htmlFor="stop-loss">Stop Loss (%)</Label>
              <Input
                id="stop-loss"
                placeholder="e.g. 2"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                className="rounded-lg"
              />
            </div>
            <div className="col-span-2 flex items-end">
              <Button 
                onClick={handleAddSymbol} 
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isValidating || !symbolInput.trim()}
              >
                {isValidating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {symbols.length > 0 ? (
        <Card>
          <CardContent className="p-4">
            <ScrollArea className="h-60 pr-3">
              <div className="space-y-2">
                {symbols.map((symbol) => (
                  <div 
                    key={symbol.id} 
                    className="grid grid-cols-12 gap-2 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="col-span-4 flex items-center gap-2">
                      {getSymbolStatusIndicator(symbol.symbol)}
                      <span className="text-lg font-semibold">{symbol.symbol}</span>
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor={`pt-${symbol.id}`} className="text-xs text-gray-500">
                        Profit Target (%)
                      </Label>
                      <div className="flex items-center">
                        <Input
                          id={`pt-${symbol.id}`}
                          value={symbol.profitTarget}
                          onChange={(e) => handleUpdateSymbol(symbol.id, 'profitTarget', e.target.value)}
                          className="rounded-lg"
                        />
                        <Percent className="h-4 w-4 text-gray-400 -ml-8" />
                      </div>
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor={`sl-${symbol.id}`} className="text-xs text-gray-500">
                        Stop Loss (%)
                      </Label>
                      <div className="flex items-center">
                        <Input
                          id={`sl-${symbol.id}`}
                          value={symbol.stopLoss}
                          onChange={(e) => handleUpdateSymbol(symbol.id, 'stopLoss', e.target.value)}
                          className="rounded-lg"
                        />
                        <Percent className="h-4 w-4 text-gray-400 -ml-8" />
                      </div>
                    </div>
                    <div className="col-span-2 flex items-center justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSymbol(symbol.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      ) : (
        <div className="flex items-center justify-center p-8 border border-dashed rounded-lg bg-gray-50">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No symbols added yet</p>
            <p className="text-sm text-gray-400">Add symbols using the form above</p>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button 
          onClick={handleSaveSymbols} 
          disabled={symbols.length === 0 || isLoading}
          className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading Market Data...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Market Data
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StewieQuantSymbolsAggregation;
